import { get } from 'lodash';

// ─── ApiError ─────────────────────────────────────────────────────────────────
// Single error class for all API failures.
// Callers catch ApiError to show user-facing messages; unexpected errors bubble up.

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

/** Wrap an unknown catch value into ApiError for uniform handling. */
export const toApiError = (e: unknown): ApiError => {
  if (isApiError(e)) return e;
  const msg = e instanceof Error ? e.message : 'An unexpected error occurred';
  return new ApiError(0, msg);
};

/**
 * Business-logic errors come back as HTTP 200 with { errCode, errDetail: { en } }.
 * Throws ApiError with the server's message when present; no-op otherwise.
 */
export const throwIfServerError = (response: unknown, fallbackMessage: string): void => {
  const errCode = get(response, 'data.errCode', null);
  const errDetail = get(response, 'data.errDetail', null);
  if (errCode && errDetail) {
    throw new ApiError(errCode, get(errDetail, 'en', null) || fallbackMessage);
  }
};
